import React, { useEffect } from "react";
import "../day-plan-styles/DayPlanControls.css";
import { useDataContext } from "../../../data-context/DataContextProvider";
import { useTranslation } from "@preppa/translation";
import { DAILY_DAYVIEW_ID } from "../../../utils/timeSegmentation";
import { setCookie, getCookie } from "../../../utils/cookieUtils";
import { formatDateForDisplay } from "../../../utils/dateUtils";
//SKAPA Components
import Text from "@ingka/text";
import Button from "@ingka/button";
import Select from "@ingka/select";
//SKAPA Icons
import chevronRight from "@ingka/ssr-icon/paths/chevron-right";
import chevronLeft from "@ingka/ssr-icon/paths/chevron-left";
import worksheetIcon from "@ingka/ssr-icon/paths/document";
//SKAPA Styles
import "@ingka/svg-icon/dist/style.css";
import "@ingka/button/dist/style.css";
import "@ingka/focus/dist/style.css";
import "@ingka/text/dist/style.css";
import "@ingka/forms/dist/style.css";

function DayPlanControls() {
  const {
    worksheets,
    defaultDayViews,
    dayViews,
    selectedWorksheet,
    selectedDate,
    dispatch,
  } = useDataContext();
  const { translate } = useTranslation();

  const today = new Date();
  const minDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  // Plan data only covers 7 days from today, and prepDayBefore worksheets read one day ahead.
  const maxDate = new Date(minDate);
  maxDate.setDate(
    maxDate.getDate() + (selectedWorksheet?.prepDayBefore ? 5 : 6),
  );

  const selectedDateOnly = new Date(
    selectedDate.getFullYear(),
    selectedDate.getMonth(),
    selectedDate.getDate(),
  );

  const isAtMinDate = selectedDateOnly.getTime() <= minDate.getTime();
  const isAtMaxDate = selectedDateOnly.getTime() >= maxDate.getTime();

  const maxDateTime = maxDate.getTime();
  const selectedDateTime = selectedDateOnly.getTime();
  useEffect(() => {
    if (selectedDateTime > maxDateTime) {
      dispatch({ type: "SET_SELECTED_DATE", payload: new Date(maxDateTime) });
    }
  }, [maxDateTime, selectedDateTime, dispatch]);

  const handleWorksheetSelection = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const worksheetName = event.target.value;
    if (worksheetName === "") {
      dispatch({ type: "SET_SELECTED_WORKSHEET", payload: null });
      setCookie("selectedWorksheet", "", 30);
    } else {
      const worksheet = worksheets.find((w) => w.name === worksheetName);
      if (worksheet) {
        dispatch({ type: "SET_SELECTED_WORKSHEET", payload: worksheet });
        setCookie("selectedWorksheet", worksheetName, 30);
      }
    }
  };

  useEffect(() => {
    if (!selectedWorksheet) {
      dispatch({ type: "SET_SELECTED_DAYVIEW", payload: null });
      return;
    }

    const dayViewId = selectedWorksheet.selectedDayViewId;
    let resolvedDayView = null;

    if (dayViewId && dayViewId !== "") {
      resolvedDayView =
        defaultDayViews.find((dv) => dv.dayViewId === dayViewId) ||
        dayViews.find((dv) => dv.dayViewId === dayViewId) ||
        defaultDayViews.find((dv) => dv.dayViewId === DAILY_DAYVIEW_ID) ||
        null;
    } else {
      resolvedDayView =
        defaultDayViews.find((dv) => dv.dayViewId === DAILY_DAYVIEW_ID) || null;
    }

    dispatch({ type: "SET_SELECTED_DAYVIEW", payload: resolvedDayView });
  }, [selectedWorksheet, defaultDayViews, dayViews, dispatch]);

  useEffect(() => {
    const savedWorksheetName = getCookie("selectedWorksheet");
    if (savedWorksheetName && worksheets.length > 0) {
      const foundWorksheet = worksheets.find(
        (w) => w.name === savedWorksheetName,
      );
      //console.log("Found worksheet:", foundWorksheet);
      if (foundWorksheet) {
        dispatch({ type: "SET_SELECTED_WORKSHEET", payload: foundWorksheet });
      }
    }
  }, [worksheets, dispatch]);

  const handlePreviousDay = () => {
    if (isAtMinDate) return;
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    dispatch({ type: "SET_SELECTED_DATE", payload: newDate });
  };

  const handleNextDay = () => {
    if (isAtMaxDate) return;
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    dispatch({ type: "SET_SELECTED_DATE", payload: newDate });
  };

  return (
    <div id="controls-wrapper">
      <div id="date-controls-container">
        <Button
          id="date-button-left"
          size="small"
          ssrIcon={chevronLeft}
          iconOnly
          type="tertiary"
          onClick={handlePreviousDay}
          disabled={isAtMinDate}
        />
        <Text tagName="span" textStyle="Caption.L">
          {formatDateForDisplay(selectedDate, translate)}
          {selectedWorksheet?.prepDayBefore && (
            <span className="prep-day-before-indicator">+1</span>
          )}
        </Text>
        <Button
          id="date-button-right"
          size="small"
          ssrIcon={chevronRight}
          iconOnly
          type="tertiary"
          onClick={handleNextDay}
          disabled={isAtMaxDate}
        />
        <Button
          id="back-to-today-button"
          size="xsmall"
          type="secondary"
          text={translate("dayPlan.controls.backToToday")}
          onClick={() =>
            dispatch({ type: "SET_SELECTED_DATE", payload: new Date() })
          }
        />
      </div>
      <div id="prep-day-before-banner-container">
        {selectedWorksheet?.prepDayBefore && (
          <div id="prep-day-before-banner">
            <Text tagName="span" textStyle="LabelRegular.L">
              {translate("dayPlan.controls.showingSalesForTomorrow")}
            </Text>
          </div>
        )}
      </div>
      <div id="day-plan-filters">
        <Select
          id="worksheet-select"
          hintText={translate("dayPlan.controls.selectWorksheet")}
          ssrIcon={worksheetIcon}
          value={selectedWorksheet?.name || ""}
          onChange={handleWorksheetSelection}
        >
          {worksheets.map((worksheet) => (
            <option key={worksheet.id} value={worksheet.name}>
              {worksheet.name}
            </option>
          ))}
        </Select>
      </div>
    </div>
  );
}

export default DayPlanControls;
